import type { PersistedEstimate } from '@hooks/use-persisted-estimate';
import type { IconName } from '@tkww/orion-web-ds-graphic';
import {
	displayCostValue,
	formatCurrency,
} from '../../hooks/use-pricing-estimation';
import type {
	EstimateDisplay,
	PricingEstimationService,
	PricingEstimationType,
	PricingEstimationTypeSource,
} from '../../types';

export type EstimateServiceBreakdownItem =
	| {
			price: string;
			sourceType: PricingEstimationTypeSource;
			isFree?: boolean;
	  }
	| { unavailable: true };

export type EstimateServiceBreakdown = Partial<
	Record<PricingEstimationService, EstimateServiceBreakdownItem | string>
>;

export const displayEstimateServicePrice = (
	pricing: Exclude<PricingEstimationType, null>,
): string => {
	return displayCostValue(
		pricing.cost != null
			? pricing
			: {
					cost: pricing.maxCost,
					unit: pricing.unit,
					currency: pricing.currency,
				},
	);
};

type EstimateServiceConfig = {
	key: PricingEstimationService;
	value: string;
	isPerGuest: boolean;
	icon: IconName;
};

export const SERVICES: EstimateServiceConfig[] = [
	{
		key: 'ceremony',
		value: 'Ceremony',
		isPerGuest: false,
		icon: 'rounded-altar',
	},
	{
		key: 'reception',
		value: 'Reception',
		isPerGuest: false,
		icon: 'champagne-glass',
	},
	{ key: 'catering', value: 'Catering', isPerGuest: true, icon: 'fork-knife' },
	{
		key: 'barServices',
		value: 'Bar service',
		isPerGuest: true,
		icon: 'glass',
	},
];

export interface CostBreakdownDisplayItem {
	key: PricingEstimationService;
	title: string;
	icon: IconName;
	cost: string | undefined;
	isFree: boolean;
	isLocalAverage: boolean;
	isStartingPrice: boolean;
	isUnavailable: boolean;
}

export const buildCostBreakdownItems = (
	costBreakdown: EstimateServiceBreakdown | null,
): CostBreakdownDisplayItem[] => {
	if (!costBreakdown) return [];

	return SERVICES.flatMap(
		({ key, value, isPerGuest, icon }): CostBreakdownDisplayItem[] => {
			const cost = costBreakdown[key];
			if (!cost) return [];

			const normalizedCost: EstimateServiceBreakdownItem =
				typeof cost === 'string' ? { price: cost, sourceType: 'VENDOR' } : cost;

			if ('unavailable' in normalizedCost) {
				return [
					{
						key,
						title: value,
						icon,
						cost: undefined,
						isFree: false,
						isLocalAverage: false,
						isStartingPrice: false,
						isUnavailable: true,
					},
				];
			}

			const rawPrice = normalizedCost.price.replace(' per guest', '');
			const priceWithGuestSuffix =
				isPerGuest && rawPrice !== 'Contact for price'
					? `${rawPrice} / guest`
					: rawPrice;
			const isLocalAverage =
				!normalizedCost.isFree && normalizedCost.sourceType !== 'VENDOR';
			const isStartingPrice =
				!normalizedCost.isFree &&
				!isLocalAverage &&
				priceWithGuestSuffix !== 'Contact for price';

			return [
				{
					key,
					title: value,
					icon,
					cost: priceWithGuestSuffix,
					isFree: normalizedCost.isFree === true,
					isLocalAverage,
					isStartingPrice,
					isUnavailable: false,
				},
			];
		},
	);
};

export const buildEstimateDisplay = ({
	selections,
	weddingDate,
	guestCount,
	calculatedTotal,
	estimateSourceType,
}: Pick<
	PersistedEstimate,
	| 'selections'
	| 'weddingDate'
	| 'guestCount'
	| 'calculatedTotal'
	| 'estimateSourceType'
>): EstimateDisplay => {
	const date =
		weddingDate.type === 'exact'
			? new Date(`${weddingDate.date}T00:00:00`).toLocaleString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				})
			: `${weddingDate.month} ${weddingDate.year}`;
	const servicesCount = Object.keys(selections).length;
	const servicesText = `${servicesCount} ${servicesCount === 1 ? 'service' : 'services'}`;
	const isBelowMinimumSpend = calculatedTotal.isBelowMinimumSpend === true;
	const { originalMinCost, min } = calculatedTotal;
	const hasValidOriginalMinCost =
		typeof originalMinCost === 'number' && Number.isFinite(originalMinCost);
	const hasValidMin = typeof min === 'number' && Number.isFinite(min);
	return {
		price: displayCostValue({
			minCost: calculatedTotal.min,
			maxCost: calculatedTotal.max,
		}),
		//Details: Jul 27, 2027, 145 guests, 4 services
		description: `Details: ${date}, ${guestCount} guests, ${servicesText}`,
		estimateSourceType,
		isBelowMinimumSpend,
		originalEstimate:
			isBelowMinimumSpend && hasValidOriginalMinCost
				? formatCurrency(originalMinCost)
				: undefined,
		minimumSpend:
			isBelowMinimumSpend && hasValidMin ? formatCurrency(min) : undefined,
	};
};
